import * as yup from 'yup';

import { useNutzInputStore } from '@/features/Sparten/nutz/useInputStore';
import {
  hasAufbauart,
  hasGefahrgut,
  hasJaehrlicheFahrleistung,
  hasKilometerstand,
  hasLeistung,
  hasNeuwert,
  hasNutzungsart,
  hasAnhaengerBauart,
  hasGesamtewicht,
  isLieferwagen,
  isLKW,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { assistenzsysteme } from '@/features/Sparten/shared/schema/kfz/assistenzSystemeSchema/assistenzSystemeSchema';

export const validateZulassungsdatumAufVN = (value: Date | null): boolean => {
  const store = useNutzInputStore();

  if (value == null || store.fahrzeug.erstbesitzer) {
    return true;
  }

  const zulassung = store.fahrzeug.erstzulassung;

  return !(zulassung !== null && value < zulassung);
};

export const minimumErstzulassungsdatum = new Date('1900-01-01');
export const validateErstzulassungsdatum = (value: Date | null): boolean =>
  value === null || value >= minimumErstzulassungsdatum;

export const fahrzeugSchema = yup.object({
  assistenzsysteme,
  auslandsnutzung: yup.string().default(''),
  erstbesitzer: yup.boolean().default(false),
  erstzulassung: yup
    .date()
    .nullable()
    .default(null)
    .required()
    .test(
      'validErstzulassungsDatum',
      'Darf nicht vor 01.01.1900 liegen',
      validateErstzulassungsdatum,
    ),
  erstmaligeZulassungAufVN: yup
    .date()
    .nullable()
    .default(null)
    .test(
      'validZulassungsDatum',
      'Darf nicht vor Erstzulassung liegen.',
      validateZulassungsdatumAufVN,
    )
    .when('erstbesitzer', {
      is: (val: boolean) => !val,
      then: yup.date().required(),
    }),
  fahrleistung: yup
    .number()
    .default(0)
    .when({
      is: () => hasJaehrlicheFahrleistung.value,
      then: yup.number().min(100).required(),
    }),
  finanzierungsart: yup.string().default(''),
  anhaengerBauart: yup
    .string()
    .default('')
    .when({
      is: () => hasAnhaengerBauart.value,
      then: yup.string().required(),
    }),
  anhaengerVerleihung: yup
    .boolean()
    .default(false)
    .when({
      is: () => hasAnhaengerBauart.value,
      then: yup.boolean().required(),
    }),
  gesellschaftZugfahrzeug: yup
    .number()
    .default(0)
    .when({
      is: () => hasAnhaengerBauart.value,
      then: yup.number().required(),
    }),
  kilometerstand: yup
    .number()
    .default(null)
    .nullable()
    .when({
      is: () => hasKilometerstand.value,
      then: yup.number().min(1).required(),
    }),
  leistung: yup
    .number()
    .default(0)
    .when({
      is: () => hasLeistung.value,
      then: yup
        .number()
        .min(1, 'Die Leistung in kW muss mindestens 1 betragen')
        .required(),
    }),
  neuwert: yup
    .number()
    .default(0)
    .when({
      is: () => hasNeuwert.value,
      then: yup.number().min(1).max(1000000).required(),
    }),
  nutzungsart: yup
    .string()
    .default('')
    .when({
      is: () => hasNutzungsart.value,
      then: yup.string().required(),
    }),
  gesamtgewicht: yup
    .number()
    .default(0)
    .when({
      is: () => hasGesamtewicht.value,
      then: yup.number().min(1).max(100000).required(),
    })
    .when({
      is: () => hasGesamtewicht.value && isLieferwagen.value,
      then: yup.number().max(3500).required(),
    })
    .when({
      is: () => hasGesamtewicht.value && isLKW.value,
      then: yup.number().min(3500).required(),
    }),
  gefahrgut: yup
    .string()
    .default('')
    .when({
      is: () => hasGefahrgut.value,
      then: yup.string().required(),
    }),
  aufbauart: yup
    .string()
    .default('')
    .when({
      is: () => hasAufbauart.value,
      then: yup.string().required(),
    }),
});
